import DOMPurify from 'dompurify';
import React, { useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import type Corp from '@r6digital/rapidstor-types/react/entities/Corp';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { MovedInReservation } from '@r6digital/rapidstor-types/types/Reservation';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import WizardDescriptor from '@/core/components/wizard/descriptor';
import MarketingSurvey from '@/core/components/wizard/forms/marketing-survey';

import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import IconClipboardFilled from '@/assets/icon-clipboard-filled.svg?react';
import IconClipboard from '@/assets/icon-clipboard.svg?react';

import classes from '@/scss/screens/move-in.module.scss';

interface Props {
    reservation: MovedInReservation | undefined;
    reservationDataLoading: boolean;
    descriptor: Descriptor | undefined;
    unitType: UnitType | undefined;
    corporationData: Corp | undefined;
}

export default function SimplifiedCheckoutConfirmed({
    reservation,
    reservationDataLoading,
    descriptor,
    unitType,
    corporationData,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const { rapidstorMainColor } = React.useContext(JourneyContext);
    const [copied, setCopied] = useState(false);

    const errorAfterMoveIn = useMemo(
        () => reservation?.ledgerResult?.errorAfterMoveIn || '',
        [reservation],
    );

    const handleAfterMoveInErrorMessage = () => {
        if (!errorAfterMoveIn) return '';
        switch (true) {
            case reservation?.ledgerResult?.failedAutobillSetup:
                return t('wizard:bookingConfirmation:autobillError');
            case reservation?.ledgerResult?.failedMerchPayment:
                return t('wizard:bookingConfirmation:merchPaymentError');
            case errorAfterMoveIn === 'Unable to assign insurance policy number':
                return t('wizard:bookingConfirmation:insuranceError');
            default:
                return errorAfterMoveIn;
        }
    };

    const clipBoardIcon = useMemo(
        () => (copied ? <IconClipboardFilled /> : <IconClipboard />),
        [copied],
    );

    const copyToClipboard = () => {
        if (reservation?.globalWaitingNum) {
            navigator.clipboard.writeText(`${reservation.globalWaitingNum}`);
            setCopied(true);
        }
    };

    const navigateToIdVerify = () => {
        const params = new URLSearchParams({
            email: reservation?.email ?? '',
            tenantId: `${reservation?.tenantId ?? ''}`,
        });
        window.location.href = `${window.rapidstor_settings.app_config.journey.idVerify?.url}?${params.toString()}`;
    };

    return (
        <div className={`${classes.container}`}>
            <div className={`${classes.header}`}>
                <span className={`${classes.title}`} style={{ flexGrow: '1' }}>
                    {t('wizard:scheduleMoveIn')}
                </span>
            </div>

            <WizardDescriptor
                descriptor={descriptor}
                unitType={unitType}
                deal={reservation?.deal ?? undefined}
            />

            <div className={`${classes.fold}`} />

            <div className={`${classes.booking_confirmation}`}>
                {reservationDataLoading ? (
                    <ContentLoader
                        foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                        backgroundColor="#fff"
                        height="156px"
                        width="100%"
                    >
                        <rect x="0" y="0" rx="5" ry="5" width="100%" height="65px" />
                        <rect x="0" y="95" rx="5" ry="5" width="100%" height="25px" />
                        <rect x="0" y="130" rx="5" ry="5" width="100%" height="25px" />
                    </ContentLoader>
                ) : (
                    <>
                        <div className={`${classes.title}`}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className={`${classes.thumbs_up}`}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    // eslint-disable-next-line max-len
                                    d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z"
                                />
                            </svg>
                            <span>{t('wizard:bookingConfirmation:title')}</span>
                        </div>
                        {t('wizard:bookingConfirmation:pleaseWait', { defaultValue: '' }) && (
                            <p className={`${classes.pleaseWait}`}>
                                {t('wizard:bookingConfirmation:pleaseWait')}
                            </p>
                        )}
                        <p className={`${classes.subtitle}`}>
                            {t('wizard:bookingConfirmation:subtitle')}
                        </p>
                        {errorAfterMoveIn && (
                            <Alert type="warning" text={handleAfterMoveInErrorMessage()} />
                        )}
                        <div className={`${classes.referenceCode}`} onClick={copyToClipboard}>
                            <span className={`${classes.text}`}>
                                {t('wizard:bookingConfirmation:referenceCode')}
                            </span>
                            <span className={`${classes.code}`}>
                                {reservation?.globalWaitingNum}
                            </span>
                            {clipBoardIcon}
                        </div>
                        {window.rapidstor_settings.app_config.journey.idVerify?.url && (
                            <div className={`${classes.id_verify_section}`}>
                                <p>{t('wizard:bookingConfirmation:idVerifyText')}</p>
                                <Button
                                    size="small"
                                    type="primary"
                                    onclick={navigateToIdVerify}
                                >
                                    {t('wizard:bookingConfirmation:idVerifyButtonText')}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className={`${classes.customer_survey}`}>
                {corporationData?.settings.storApp?.enabled && (
                    <>
                        <div className={`${classes.fold}`} />
                        <span
                            className={`${classes.app_download_text}`}
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(corporationData.settings.storApp.blurbText),
                            }}
                        />
                        <div className={`${classes.store_images}`}>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <img
                                alt="Google Play"
                                src="https://ik.imagekit.io/bytcx9plm/PlayStore.png?updatedAt=1726538026462"
                                onClick={() => {
                                    window.open(corporationData.settings.storApp!.links.googlePlay, '_blank', 'noopener,noreferrer');
                                }}
                            />
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <img
                                alt="Apple Store"
                                src="https://ik.imagekit.io/bytcx9plm/AppleStore.png?updatedAt=1726538026336"
                                onClick={() => {
                                    window.open(corporationData.settings.storApp!.links.iosAppStore, '_blank', 'noopener,noreferrer');
                                }}
                            />
                        </div>
                    </>
                )}
                {location.settings.marketingSurvey.use && reservation?.tenantId && (
                    <>
                        <div className={`${classes.fold}`} />
                        <MarketingSurvey
                            marketingSurvey={location.settings.marketingSurvey}
                            tenantId={reservation.tenantId}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
